/**
 * Maps raw origin/tipo/ORIGEM values to a human-readable app/website label.
 * Sources: pg_pagamentos_sisp.tipo (SISP), pg_sisp_pedido.ORIGEM (legacy), referer URL.
 */

interface OriginInfo {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
}

const TIPO_MAP: Record<string, OriginInfo> = {
  // Events.cv
  ticket_purchase:    { label: 'Events.cv',    color: 'info' },
  evt:                { label: 'Events.cv',    color: 'info' },
  event:              { label: 'Events.cv',    color: 'info' },
  events:             { label: 'Events.cv',    color: 'info' },
  // Pagali Shop
  shop_order:         { label: 'Pagali Shop',  color: 'secondary' },
  shop:               { label: 'Pagali Shop',  color: 'secondary' },
  ecommerce:          { label: 'Pagali Shop',  color: 'secondary' },
  'e-commerce':       { label: 'Pagali Shop',  color: 'secondary' },
  // Cashless NFC
  cashless:           { label: 'Cashless NFC', color: 'primary' },
  recarga_cashless:   { label: 'Cashless NFC', color: 'primary' },
  nfc:                { label: 'Cashless NFC', color: 'primary' },
  // Pagali Pay (Direct to Pay)
  d2p:                { label: 'Pagali Pay',   color: 'primary' },
  pagali_pay:         { label: 'Pagali Pay',   color: 'primary' },
  direct_to_pay:      { label: 'Pagali Pay',   color: 'primary' },
  // App / Wallet
  wallet:             { label: 'App Pagali',   color: 'primary' },
  app:                { label: 'App Pagali',   color: 'primary' },
  mobile:             { label: 'App Pagali',   color: 'primary' },
  // Recarga
  recarga:            { label: 'Recarga',      color: 'warning' },
  topup:              { label: 'Recarga',      color: 'warning' },
  // PagaMe / link de pagamento
  pagame:             { label: 'PagaMe',       color: 'secondary' },
  link:               { label: 'PagaMe',       color: 'secondary' },
  payment_link:       { label: 'PagaMe',       color: 'secondary' },
  // iFundHere
  ifundhere:          { label: 'iFundHere',    color: 'success' },
  crowdfunding:       { label: 'iFundHere',    color: 'success' },
  donation:           { label: 'Doação',       color: 'success' },
  doacao:             { label: 'Doação',       color: 'success' },
  // iMenu
  imenu:              { label: 'iMenu',        color: 'warning' },
  food:               { label: 'iMenu',        color: 'warning' },
  restaurant:         { label: 'iMenu',        color: 'warning' },
  // WAG
  wag:                { label: 'WAG',          color: 'secondary' },
  // API / integração
  api:                { label: 'API',          color: 'default' },
  integration:        { label: 'Integração',   color: 'default' },
  // POS
  pos:                { label: 'POS',          color: 'default' },
  // Web genérico
  web:                { label: 'Web',          color: 'default' },
};

const REFERER_DOMAINS: Array<[string, OriginInfo]> = [
  ['events.cv',     { label: 'Events.cv',   color: 'info' }],
  ['eventscv',      { label: 'Events.cv',   color: 'info' }],
  ['ifundhere',     { label: 'iFundHere',   color: 'success' }],
  ['imenu',         { label: 'iMenu',       color: 'warning' }],
  ['wag.',          { label: 'WAG',         color: 'secondary' }],
  ['pagali',        { label: 'Pagali Web',  color: 'primary' }],
];

export function getOriginInfo(
  origin?: string,
  source?: string,
  referer?: string,
  platform?: string,
): OriginInfo {
  const raw = (origin || '').toLowerCase().trim();

  // O WhatsApp ganha ao tipo do pagamento: para quem lê a tabela, a origem de um
  // pagamento feito na conversa é o WhatsApp, não «Pagali». Sem isto caía no
  // rótulo genérico do fim, porque o tipo é SERVICE_PAYMENT como no site.
  if ((platform || '').toLowerCase() === 'whatsapp') return { label: 'WhatsApp', color: 'success' };

  // Direct match in tipo map
  if (raw && TIPO_MAP[raw]) return TIPO_MAP[raw];

  // Partial match (e.g. "ticket_purchase_12345")
  for (const [key, info] of Object.entries(TIPO_MAP)) {
    if (raw.includes(key)) return info;
  }

  // Fallback: referer domain
  if (referer) {
    const ref = referer.toLowerCase();
    for (const [domain, info] of REFERER_DOMAINS) {
      if (ref.includes(domain)) return info;
    }
  }

  // Fallback: platform (SISP/Vinti4 is the payment method, not the origin)
  const plat = (platform || '').toLowerCase();
  if (plat === 'mobile' || plat === 'app') return { label: 'App Pagali', color: 'primary' };
  if (plat === 'web') return { label: 'Pagali Web', color: 'primary' };

  // Last resort: Pagali (origin unknown — method was SISP/Vinti4 but that's not the origin)
  return { label: 'Pagali', color: 'default' };
}
